import { create } from 'zustand'
import { critterApi } from '../services/critterApi'
import { critterTypeApi } from '../services/critterTypeApi'
import { modelApi } from '../services/modelApi'
import useSettingsStore from './settingsStore'

// Default critter types (fallback when API is unavailable)
export const DEFAULT_CRITTER_TYPES = {
  cat: {
    name: 'Cat',
    mouthClosed: '/assets/cat_mouth_closed.png',
    mouthOpen: '/assets/cat_mouth_open.png'
  },
  bunny: {
    name: 'Bunny',
    mouthClosed: '/assets/bunny_mouth_closed.png',
    mouthOpen: '/assets/bunny_mouth_open.png'
  },
  owl: {
    name: 'Owl',
    mouthClosed: '/assets/owl_mouth_closed.png',
    mouthOpen: '/assets/owl_mouth_open.png'
  },
  slime: {
    name: 'Slime',
    mouthClosed: '/assets/slime_mouth_closed.png',
    mouthOpen: '/assets/slime_mouth_open.png'
  },
  fox: {
    name: 'Fox',
    mouthClosed: '/assets/fox_mouth_closed.png',
    mouthOpen: '/assets/fox_mouth_open.png'
  },
  penguin: {
    name: 'Penguin',
    mouthClosed: '/assets/penguin_mouth_closed.png',
    mouthOpen: '/assets/penguin_mouth_open.png'
  },
  hamster: {
    name: 'Hamster',
    mouthClosed: '/assets/hamster_mouth_closed.png',
    mouthOpen: '/assets/hamster_mouth_open.png'
  },
  // 3D animal types
  buffalo: { name: 'Buffalo', mouthClosed: '/assets/3d_placeholder.svg', mouthOpen: '/assets/3d_placeholder.svg' },
  chick: { name: 'Chick', mouthClosed: '/assets/3d_placeholder.svg', mouthOpen: '/assets/3d_placeholder.svg' },
  cow: { name: 'Cow', mouthClosed: '/assets/3d_placeholder.svg', mouthOpen: '/assets/3d_placeholder.svg' },
  donkey: { name: 'Donkey', mouthClosed: '/assets/3d_placeholder.svg', mouthOpen: '/assets/3d_placeholder.svg' },
  duck: { name: 'Duck', mouthClosed: '/assets/3d_placeholder.svg', mouthOpen: '/assets/3d_placeholder.svg' },
  hen: { name: 'Hen', mouthClosed: '/assets/3d_placeholder.svg', mouthOpen: '/assets/3d_placeholder.svg' },
  pig: { name: 'Pig', mouthClosed: '/assets/3d_placeholder.svg', mouthOpen: '/assets/3d_placeholder.svg' },
  rooster: { name: 'Rooster', mouthClosed: '/assets/3d_placeholder.svg', mouthOpen: '/assets/3d_placeholder.svg' },
  sheep: { name: 'Sheep', mouthClosed: '/assets/3d_placeholder.svg', mouthOpen: '/assets/3d_placeholder.svg' },
  crow: { name: 'Crow', mouthClosed: '/assets/3d_placeholder.svg', mouthOpen: '/assets/3d_placeholder.svg' },
  eagle: { name: 'Eagle', mouthClosed: '/assets/3d_placeholder.svg', mouthOpen: '/assets/3d_placeholder.svg' },
  hog: { name: 'Hog', mouthClosed: '/assets/3d_placeholder.svg', mouthOpen: '/assets/3d_placeholder.svg' },
  hornbill: { name: 'Hornbill', mouthClosed: '/assets/3d_placeholder.svg', mouthOpen: '/assets/3d_placeholder.svg' },
  raccoon: { name: 'Raccoon', mouthClosed: '/assets/3d_placeholder.svg', mouthOpen: '/assets/3d_placeholder.svg' },
  snake: { name: 'Snake', mouthClosed: '/assets/3d_placeholder.svg', mouthOpen: '/assets/3d_placeholder.svg' },
  wolf: { name: 'Wolf', mouthClosed: '/assets/3d_placeholder.svg', mouthOpen: '/assets/3d_placeholder.svg' },
  dog: { name: 'Dog', mouthClosed: '/assets/3d_placeholder.svg', mouthOpen: '/assets/3d_placeholder.svg' },
  dove: { name: 'Dove', mouthClosed: '/assets/3d_placeholder.svg', mouthOpen: '/assets/3d_placeholder.svg' },
  goldfish: { name: 'Goldfish', mouthClosed: '/assets/3d_placeholder.svg', mouthOpen: '/assets/3d_placeholder.svg' },
  mice: { name: 'Mice', mouthClosed: '/assets/3d_placeholder.svg', mouthOpen: '/assets/3d_placeholder.svg' },
  parrot: { name: 'Parrot', mouthClosed: '/assets/3d_placeholder.svg', mouthOpen: '/assets/3d_placeholder.svg' },
  pigeon: { name: 'Pigeon', mouthClosed: '/assets/3d_placeholder.svg', mouthOpen: '/assets/3d_placeholder.svg' },
  rabbit: { name: 'Rabbit', mouthClosed: '/assets/3d_placeholder.svg', mouthOpen: '/assets/3d_placeholder.svg' },
  tortoise: { name: 'Tortoise', mouthClosed: '/assets/3d_placeholder.svg', mouthOpen: '/assets/3d_placeholder.svg' }
}

// Mutable critter types that gets updated from API
export let CRITTER_TYPES = { ...DEFAULT_CRITTER_TYPES }

// Helper to get current settings
const getSettings = () => useSettingsStore.getState().settings

// Collision detection - minimum distance between critters (percentage of viewport)
const CRITTER_COLLISION_DISTANCE = 8 // ~8% viewport width, matches critter size

// Check if a position overlaps with any existing critter
const isPositionOverlapping = (x, y, critters, excludeUserId = null) => {
  for (const [userId, critter] of Object.entries(critters)) {
    if (userId === excludeUserId) continue
    if (critter.despawning) continue

    const dx = x - critter.position.x
    const dy = y - critter.position.y
    const distance = Math.sqrt(dx * dx + dy * dy)

    if (distance < CRITTER_COLLISION_DISTANCE) {
      return true
    }
  }
  return false
}

// Find a non-overlapping position within bounds
const findNonOverlappingPosition = (critters, settings, excludeUserId = null, maxAttempts = 20) => {
  for (let attempt = 0; attempt < maxAttempts; attempt++) {
    const x = settings.minX + Math.random() * (settings.maxX - settings.minX)
    const y = settings.minY + Math.random() * (settings.maxY - settings.minY)

    if (!isPositionOverlapping(x, y, critters, excludeUserId)) {
      return { x, y }
    }
  }

  // Fallback: use grid-based positioning if random attempts fail
  const critterCount = Object.keys(critters).length
  const cols = Math.ceil(Math.sqrt(critterCount + 1))
  const cellWidth = (settings.maxX - settings.minX) / cols
  const cellHeight = (settings.maxY - settings.minY) / cols

  // Find first empty cell
  for (let row = 0; row < cols; row++) {
    for (let col = 0; col < cols; col++) {
      const x = settings.minX + (col + 0.5) * cellWidth
      const y = settings.minY + (row + 0.5) * cellHeight

      if (!isPositionOverlapping(x, y, critters, excludeUserId)) {
        return { x, y }
      }
    }
  }

  // Last resort: return random position even if overlapping
  return {
    x: settings.minX + Math.random() * (settings.maxX - settings.minX),
    y: settings.minY + Math.random() * (settings.maxY - settings.minY)
  }
}

export const useCritterStore = create((set, get) => ({
  critters: {},
  critterTypes: { ...DEFAULT_CRITTER_TYPES },
  roamingInterval: null,
  updateInterval: null,
  threeDRoamingInterval: null,
  threeDUpdateInterval: null,
  sleepCheckInterval: null,
  broadcasterLogin: null,

  // Set broadcaster context
  setBroadcasterLogin: (login) => {
    critterApi.setBroadcasterLogin(login)
    critterTypeApi.setBroadcasterLogin(login)
    set({ broadcasterLogin: login })
  },

  // Load critter types from API
  loadCritterTypes: async () => {
    const { broadcasterLogin } = get()
    if (!broadcasterLogin) return

    try {
      critterTypeApi.setBroadcasterLogin(broadcasterLogin)
      const types = await critterTypeApi.getAllTypes()

      if (Object.keys(types).length > 0) {
        // Fetch 3D model data and merge into critter types
        try {
          modelApi.setBroadcasterLogin(broadcasterLogin)
          const models = await modelApi.getAllModels()

          models.forEach((model) => {
            const type = model.critter_type
            if (types[type]) {
              types[type] = {
                ...types[type],
                renderMode: '3d',
                modelUrl: model.model_url,
                animationMap: model.animation_map || {},
                modelScale: model.model_scale || 5.0
              }
            }
          })
        } catch (modelError) {
          console.error('Failed to load 3D models:', modelError)
          // Continue without 3D data
        }

        // Update the mutable CRITTER_TYPES for backward compatibility
        CRITTER_TYPES = { ...types }
        set({ critterTypes: types })
        console.log(`Loaded ${Object.keys(types).length} critter types`)
      }
    } catch (error) {
      console.error('Failed to load critter types:', error)
      // Keep default types on error
    }
  },

  // Get critter types (from store state)
  getCritterTypes: () => get().critterTypes,

  // Start roaming behavior
  startRoaming: () => {
    const { roamingInterval, updateInterval, threeDRoamingInterval, threeDUpdateInterval } = get()
    const settings = getSettings()

    // Clear existing intervals
    if (roamingInterval) clearInterval(roamingInterval)
    if (updateInterval) clearInterval(updateInterval)
    if (threeDRoamingInterval) clearInterval(threeDRoamingInterval)
    if (threeDUpdateInterval) clearInterval(threeDUpdateInterval)

    let newRoamingInterval = null
    let newUpdateInterval = null
    let new3DRoamingInterval = null
    let new3DUpdateInterval = null

    // 2D roaming (if not static)
    if (!settings.staticMode) {
      newRoamingInterval = setInterval(() => {
        get().pickNewTargets('2d')
      }, settings.roamInterval)

      newUpdateInterval = setInterval(() => {
        get().updatePositions('2d')
      }, settings.updateInterval)
    }

    // 3D roaming (independent settings)
    if (!settings.threeDStaticMode) {
      new3DRoamingInterval = setInterval(() => {
        get().pickNewTargets('3d')
      }, settings.threeDRoamInterval)

      new3DUpdateInterval = setInterval(() => {
        get().updatePositions('3d')
      }, settings.threeDUpdateInterval)
    }

    set({
      roamingInterval: newRoamingInterval,
      updateInterval: newUpdateInterval,
      threeDRoamingInterval: new3DRoamingInterval,
      threeDUpdateInterval: new3DUpdateInterval
    })

    // Pick initial targets
    if (!settings.staticMode) get().pickNewTargets('2d')
    if (!settings.threeDStaticMode) get().pickNewTargets('3d')
  },

  // Stop roaming
  stopRoaming: () => {
    const { roamingInterval, updateInterval, threeDRoamingInterval, threeDUpdateInterval } = get()
    if (roamingInterval) clearInterval(roamingInterval)
    if (updateInterval) clearInterval(updateInterval)
    if (threeDRoamingInterval) clearInterval(threeDRoamingInterval)
    if (threeDUpdateInterval) clearInterval(threeDUpdateInterval)
    set({ roamingInterval: null, updateInterval: null, threeDRoamingInterval: null, threeDUpdateInterval: null })
  },

  // Pick new random target positions for critters (avoiding overlap)
  // mode: '2d' | '3d' | undefined (all)
  pickNewTargets: (mode) => {
    const settings = getSettings()
    set((state) => {
      const newCritters = { ...state.critters }

      // Create a map of planned target positions to check against
      const plannedTargets = {}

      Object.keys(newCritters).forEach((userId) => {
        const critter = newCritters[userId]
        if (!critter || critter.despawning || critter.isSleeping) return

        // Filter by mode
        const critterType = state.critterTypes[critter.critterType]
        const is3D = critterType?.renderMode === '3d'
        if (mode === '2d' && is3D) return
        if (mode === '3d' && !is3D) return

        if (true) {
          // Find a non-overlapping target position
          // Check against both current positions and already-planned targets
          const combinedPositions = { ...newCritters }

          // Add planned targets as pseudo-critters for collision checking
          Object.entries(plannedTargets).forEach(([id, target]) => {
            if (combinedPositions[id]) {
              combinedPositions[id] = {
                ...combinedPositions[id],
                position: { x: target.x, y: target.y }
              }
            }
          })

          const { x, y } = findNonOverlappingPosition(combinedPositions, settings, userId)

          // 3D critters only move horizontally (keep Y fixed)
          const critterType = state.critterTypes[critter.critterType]
          const is3D = critterType?.renderMode === '3d'
          const finalY = is3D ? critter.position.y : y

          plannedTargets[userId] = { x, y: finalY }

          newCritters[userId] = {
            ...critter,
            targetX: x,
            targetY: finalY
          }
        }
      })

      return { critters: newCritters }
    })
  },

  // Update positions towards targets (with collision avoidance)
  // mode: '2d' | '3d' | undefined (all)
  updatePositions: (mode) => {
    const settings = getSettings()
    set((state) => {
      const newCritters = { ...state.critters }
      let changed = false

      Object.keys(newCritters).forEach((userId) => {
        const critter = newCritters[userId]
        if (!critter || critter.targetX === undefined || critter.despawning) return

        // Filter by mode
        const critterType = state.critterTypes[critter.critterType]
        const is3D = critterType?.renderMode === '3d'
        if (mode === '2d' && is3D) return
        if (mode === '3d' && !is3D) return

        if (true) {
          const dx = critter.targetX - critter.position.x
          const dy = critter.targetY - critter.position.y
          const distance = Math.sqrt(dx * dx + dy * dy)

          // Only move if we're not at the target
          if (distance > 0.5) {
            // Use 3D-specific speed setting for 3D critters
            const speed = is3D ? settings.threeDRoamSpeed : settings.roamSpeed

            const moveX = (dx / distance) * speed
            const moveY = (dy / distance) * speed

            const newX = critter.position.x + moveX
            const newY = critter.position.y + moveY

            // Check if new position would cause collision
            const wouldCollide = isPositionOverlapping(newX, newY, newCritters, userId)

            // Determine facing direction based on movement
            const facingLeft = dx < 0

            if (!wouldCollide) {
              // Move normally if no collision
              newCritters[userId] = {
                ...critter,
                position: { x: newX, y: newY },
                facingLeft,
                isWalking: true
              }
              changed = true
            } else {
              // Stop and pick new target if would collide
              newCritters[userId] = {
                ...critter,
                targetX: critter.position.x,
                targetY: critter.position.y,
                isWalking: false
              }
              changed = true
            }
          } else if (critter.isWalking) {
            // Stop walking animation when at target
            newCritters[userId] = {
              ...critter,
              isWalking: false
            }
            changed = true
          }
        }
      })

      return changed ? { critters: newCritters } : state
    })
  },

  // Add a new critter (syncs to database)
  addCritter: async (userId, username, critterType) => {
    const { critters, roamingInterval, broadcasterLogin } = get()
    const settings = getSettings()

    // Check if already exists
    if (critters[userId]) {
      console.log(`${username} already has a critter`)
      return false
    }

    // Check max limit
    if (Object.keys(critters).length >= settings.maxCritters) {
      console.log('Maximum critters reached')
      return false
    }

    // Validate critter type, use default from settings
    const type = CRITTER_TYPES[critterType] ? critterType : settings.defaultCritterType

    // Sync to database if broadcaster is set
    if (broadcasterLogin) {
      const saved = await critterApi.addCritter(userId, username, type)
      if (!saved) {
        console.error('Failed to save critter to database')
        return false
      }
    }

    // Find a non-overlapping position for the new critter
    const { x, y } = findNonOverlappingPosition(critters, settings)

    const newCritter = {
      userId,
      username,
      critterType: type,
      position: { x, y },
      targetX: x,
      targetY: y,
      facingLeft: Math.random() > 0.5,
      isWalking: false,
      mouthOpen: false,
      isFeeding: false,
      isPetting: false,
      isDancing: false,
      isDoingTrick: false,
      isHugging: false,
      hugTarget: null,
      isBonking: false,
      bonkTarget: null,
      chatMessage: null,
      spawning: true,
      despawning: false,
      lastActivity: Date.now(),
      isSleeping: false
    }

    set((state) => ({
      critters: { ...state.critters, [userId]: newCritter }
    }))

    // Start roaming if not already started
    if (!roamingInterval) {
      get().startRoaming()
    }

    // Remove spawning state after animation
    setTimeout(() => {
      set((state) => ({
        critters: {
          ...state.critters,
          [userId]: state.critters[userId]
            ? { ...state.critters[userId], spawning: false }
            : state.critters[userId]
        }
      }))
    }, settings.spawnDuration)

    console.log(`Created critter for ${username}`)
    return true
  },

  // Remove a critter (syncs to database)
  removeCritter: async (userId) => {
    const { critters, broadcasterLogin } = get()
    const settings = getSettings()
    if (!critters[userId]) return false

    // Set despawning state
    set((state) => ({
      critters: {
        ...state.critters,
        [userId]: { ...state.critters[userId], despawning: true }
      }
    }))

    // Sync to database if broadcaster is set
    if (broadcasterLogin) {
      await critterApi.removeCritter(userId)
    }

    // Actually remove after animation
    setTimeout(() => {
      set((state) => {
        const newCritters = { ...state.critters }
        delete newCritters[userId]

        // Stop roaming if no critters left
        if (Object.keys(newCritters).length === 0) {
          get().stopRoaming()
        }

        return { critters: newCritters }
      })
    }, settings.despawnDuration)

    return true
  },

  // Remove critter by username (for admin commands)
  removeCritterByUsername: async (username) => {
    const { critters, broadcasterLogin } = get()
    const settings = getSettings()

    // Find critter by username
    const critterEntry = Object.entries(critters).find(
      ([, c]) => c.username.toLowerCase() === username.toLowerCase()
    )

    if (!critterEntry) return false

    const [userId] = critterEntry

    // Set despawning state
    set((state) => ({
      critters: {
        ...state.critters,
        [userId]: { ...state.critters[userId], despawning: true }
      }
    }))

    // Sync to database if broadcaster is set
    if (broadcasterLogin) {
      await critterApi.removeCritterByUsername(username)
    }

    // Actually remove after animation
    setTimeout(() => {
      set((state) => {
        const newCritters = { ...state.critters }
        delete newCritters[userId]

        if (Object.keys(newCritters).length === 0) {
          get().stopRoaming()
        }

        return { critters: newCritters }
      })
    }, settings.despawnDuration)

    return true
  },

  // Remove all critters (syncs to database)
  removeAll: async () => {
    const { broadcasterLogin } = get()

    get().stopRoaming()
    set({ critters: {} })

    // Sync to database if broadcaster is set
    if (broadcasterLogin) {
      await critterApi.removeAllCritters()
    }
  },

  // Get a critter
  getCritter: (userId) => get().critters[userId],

  // Check if user has a critter (by userId or username)
  hasCritter: (userId, username) => {
    const { critters } = get()
    // First check by direct userId
    if (critters[userId]) {
      console.log(`hasCritter: Found by userId=${userId}`)
      return true
    }
    // Then check by username (for manually added critters)
    if (username) {
      const found = Object.values(critters).some(
        c => c.username.toLowerCase() === username.toLowerCase()
      )
      console.log(`hasCritter: Searching by username=${username}, found=${found}`)
      return found
    }
    console.log(`hasCritter: Not found for userId=${userId}, username=${username}`)
    return false
  },

  // Get critter by userId or username
  getCritterByUserIdOrUsername: (userId, username) => {
    const { critters } = get()
    console.log(`getCritterByUserIdOrUsername: Looking for userId=${userId}, username=${username}`)
    console.log(`getCritterByUserIdOrUsername: Available critters:`, Object.keys(critters))
    // First try direct userId lookup
    if (critters[userId]) {
      console.log(`getCritterByUserIdOrUsername: Found by userId`)
      return { id: userId, critter: critters[userId] }
    }
    // Then try username lookup (case-insensitive)
    const entry = Object.entries(critters).find(
      ([, c]) => c.username.toLowerCase() === username?.toLowerCase()
    )
    if (entry) {
      console.log(`getCritterByUserIdOrUsername: Found by username, critterId=${entry[0]}`)
    } else {
      console.log(`getCritterByUserIdOrUsername: Not found`)
    }
    return entry ? { id: entry[0], critter: entry[1] } : null
  },

  // Update critter type (syncs to database)
  setCritterType: async (userId, newType) => {
    const { broadcasterLogin } = get()

    if (!CRITTER_TYPES[newType]) return false

    // Sync to database if broadcaster is set
    if (broadcasterLogin) {
      const updated = await critterApi.updateCritterType(userId, newType)
      if (!updated) {
        console.error('Failed to update critter type in database')
        return false
      }
    }

    set((state) => ({
      critters: {
        ...state.critters,
        [userId]: state.critters[userId]
          ? { ...state.critters[userId], critterType: newType }
          : state.critters[userId]
      }
    }))
    return true
  },

  // Feed a critter (supports username-based lookup for manually added critters)
  feedCritter: (userId, username) => {
    const settings = getSettings()
    if (!settings.allowFeeding) return

    const result = get().getCritterByUserIdOrUsername(userId, username)
    if (!result) {
      console.log(`feedCritter: No critter found for userId=${userId}, username=${username}`)
      return
    }
    console.log(`feedCritter: Found critter with id=${result.id} for userId=${userId}, username=${username}`)

    const critterId = result.id

    set((state) => ({
      critters: {
        ...state.critters,
        [critterId]: { ...state.critters[critterId], mouthOpen: true, isFeeding: true }
      }
    }))

    // Reset after animation
    setTimeout(() => {
      set((state) => ({
        critters: {
          ...state.critters,
          [critterId]: state.critters[critterId]
            ? { ...state.critters[critterId], mouthOpen: false, isFeeding: false }
            : state.critters[critterId]
        }
      }))
    }, settings.feedDuration)
  },

  // Pet a critter (supports username-based lookup for manually added critters)
  petCritter: (userId, username) => {
    const settings = getSettings()
    if (!settings.allowPetting) return

    const result = get().getCritterByUserIdOrUsername(userId, username)
    if (!result) {
      console.log(`petCritter: No critter found for userId=${userId}, username=${username}`)
      return
    }
    console.log(`petCritter: Found critter with id=${result.id} for userId=${userId}, username=${username}`)

    const critterId = result.id

    set((state) => ({
      critters: {
        ...state.critters,
        [critterId]: { ...state.critters[critterId], isPetting: true }
      }
    }))

    // Reset after animation
    setTimeout(() => {
      set((state) => ({
        critters: {
          ...state.critters,
          [critterId]: state.critters[critterId]
            ? { ...state.critters[critterId], isPetting: false }
            : state.critters[critterId]
        }
      }))
    }, settings.petDuration)
  },

  // Dance - critter spins with music notes
  danceCritter: (userId, username) => {
    const settings = getSettings()
    if (!settings.allowDancing) return

    const result = get().getCritterByUserIdOrUsername(userId, username)
    if (!result) return

    const critterId = result.id

    set((state) => ({
      critters: {
        ...state.critters,
        [critterId]: { ...state.critters[critterId], isDancing: true }
      }
    }))

    setTimeout(() => {
      set((state) => ({
        critters: {
          ...state.critters,
          [critterId]: state.critters[critterId]
            ? { ...state.critters[critterId], isDancing: false }
            : state.critters[critterId]
        }
      }))
    }, settings.danceDuration)
  },

  // Trick - critter does a flip with sparkles
  trickCritter: (userId, username) => {
    const settings = getSettings()
    if (!settings.allowTricks) return

    const result = get().getCritterByUserIdOrUsername(userId, username)
    if (!result) return

    const critterId = result.id

    set((state) => ({
      critters: {
        ...state.critters,
        [critterId]: { ...state.critters[critterId], isDoingTrick: true }
      }
    }))

    setTimeout(() => {
      set((state) => ({
        critters: {
          ...state.critters,
          [critterId]: state.critters[critterId]
            ? { ...state.critters[critterId], isDoingTrick: false }
            : state.critters[critterId]
        }
      }))
    }, settings.trickDuration)
  },

  // Hug - critter walks toward target critter, both show hearts
  hugCritter: (userId, username, targetUsername) => {
    const settings = getSettings()
    if (!settings.allowHugging) return

    const result = get().getCritterByUserIdOrUsername(userId, username)
    if (!result) return

    // Find target critter by username
    const { critters } = get()
    const targetEntry = Object.entries(critters).find(
      ([, c]) => c.username.toLowerCase() === targetUsername.toLowerCase()
    )
    if (!targetEntry) return

    const critterId = result.id
    const [targetId, targetCritter] = targetEntry

    // Move the hugger toward the target
    set((state) => ({
      critters: {
        ...state.critters,
        [critterId]: {
          ...state.critters[critterId],
          isHugging: true,
          hugTarget: targetUsername,
          targetX: targetCritter.position.x,
          targetY: targetCritter.position.y,
          facingLeft: targetCritter.position.x < state.critters[critterId].position.x
        },
        [targetId]: {
          ...state.critters[targetId],
          isHugging: true,
          hugTarget: username
        }
      }
    }))

    setTimeout(() => {
      set((state) => ({
        critters: {
          ...state.critters,
          [critterId]: state.critters[critterId]
            ? { ...state.critters[critterId], isHugging: false, hugTarget: null }
            : state.critters[critterId],
          [targetId]: state.critters[targetId]
            ? { ...state.critters[targetId], isHugging: false, hugTarget: null }
            : state.critters[targetId]
        }
      }))
    }, settings.hugDuration)
  },

  // Bonk - playful hit on another critter with star effects
  bonkCritter: (userId, username, targetUsername) => {
    const settings = getSettings()
    if (!settings.allowBonking) return

    const result = get().getCritterByUserIdOrUsername(userId, username)
    if (!result) return

    // Find target critter by username
    const { critters } = get()
    const targetEntry = Object.entries(critters).find(
      ([, c]) => c.username.toLowerCase() === targetUsername.toLowerCase()
    )
    if (!targetEntry) return

    const critterId = result.id
    const [targetId] = targetEntry

    set((state) => ({
      critters: {
        ...state.critters,
        [critterId]: {
          ...state.critters[critterId],
          bonkTarget: targetUsername
        },
        [targetId]: {
          ...state.critters[targetId],
          isBonking: true
        }
      }
    }))

    setTimeout(() => {
      set((state) => ({
        critters: {
          ...state.critters,
          [critterId]: state.critters[critterId]
            ? { ...state.critters[critterId], bonkTarget: null }
            : state.critters[critterId],
          [targetId]: state.critters[targetId]
            ? { ...state.critters[targetId], isBonking: false }
            : state.critters[targetId]
        }
      }))
    }, settings.bonkDuration)
  },

  // Feast - feed all critters at once (admin)
  rainFood: () => {
    const settings = getSettings()
    const { critters } = get()

    Object.keys(critters).forEach((critterId) => {
      set((state) => ({
        critters: {
          ...state.critters,
          [critterId]: state.critters[critterId]
            ? { ...state.critters[critterId], mouthOpen: true, isFeeding: true }
            : state.critters[critterId]
        }
      }))
    })

    setTimeout(() => {
      set((state) => {
        const newCritters = { ...state.critters }
        Object.keys(newCritters).forEach((critterId) => {
          if (newCritters[critterId]) {
            newCritters[critterId] = { ...newCritters[critterId], mouthOpen: false, isFeeding: false }
          }
        })
        return { critters: newCritters }
      })
    }, settings.feedDuration)
  },

  // Show chat bubble (supports username-based lookup for manually added critters)
  showChatBubble: (userId, message, username) => {
    const settings = getSettings()
    if (!settings.allowChatBubbles) return

    const result = get().getCritterByUserIdOrUsername(userId, username)
    if (!result) return

    const critterId = result.id

    set((state) => ({
      critters: {
        ...state.critters,
        [critterId]: { ...state.critters[critterId], chatMessage: message, mouthOpen: true }
      }
    }))

    // Hide after duration
    setTimeout(() => {
      set((state) => ({
        critters: {
          ...state.critters,
          [critterId]: state.critters[critterId]
            ? { ...state.critters[critterId], chatMessage: null, mouthOpen: false }
            : state.critters[critterId]
        }
      }))
    }, settings.chatBubbleDuration)
  },

  // Load critters from API (database)
  loadFromApi: async (broadcasterLogin) => {
    try {
      const settings = getSettings()
      critterApi.setBroadcasterLogin(broadcasterLogin)
      const data = await critterApi.loadCritters()
      const critters = {}

      data.forEach((item) => {
        // Find a non-overlapping position for each critter
        const { x, y } = findNonOverlappingPosition(critters, settings)

        critters[item.userId] = {
          userId: item.userId,
          username: item.username,
          critterType: item.critterType || settings.defaultCritterType,
          position: { x, y },
          targetX: x,
          targetY: y,
          facingLeft: Math.random() > 0.5,
          isWalking: false,
          mouthOpen: false,
          isFeeding: false,
          isPetting: false,
          isDancing: false,
          isDoingTrick: false,
          isHugging: false,
          hugTarget: null,
          isBonking: false,
          bonkTarget: null,
          chatMessage: null,
          spawning: false,
          despawning: false,
          lastActivity: Date.now(),
          isSleeping: settings.enableSleepMode // Start sleeping if sleep mode is on (they're inactive)
        }
      })

      set({ critters, broadcasterLogin })
      console.log(`Loaded ${Object.keys(critters).length} critters from database`)

      // Start roaming after loading
      if (Object.keys(critters).length > 0) {
        get().startRoaming()
      }
    } catch (error) {
      console.error('Failed to load critters:', error)
    }
  },

  // Wake a critter (mark as active)
  wakeCritter: (userId, username) => {
    const result = get().getCritterByUserIdOrUsername(userId, username)
    if (!result) return

    const critterId = result.id
    set((state) => ({
      critters: {
        ...state.critters,
        [critterId]: state.critters[critterId]
          ? { ...state.critters[critterId], isSleeping: false, lastActivity: Date.now() }
          : state.critters[critterId]
      }
    }))
  },

  // Update last activity timestamp for a critter
  touchCritter: (userId, username) => {
    const result = get().getCritterByUserIdOrUsername(userId, username)
    if (!result) return

    const critterId = result.id
    const critter = get().critters[critterId]
    if (!critter) return

    const updates = { lastActivity: Date.now() }
    if (critter.isSleeping) {
      updates.isSleeping = false
    }

    set((state) => ({
      critters: {
        ...state.critters,
        [critterId]: state.critters[critterId]
          ? { ...state.critters[critterId], ...updates }
          : state.critters[critterId]
      }
    }))
  },

  // Check all critters and put inactive ones to sleep
  checkSleepStates: () => {
    const settings = getSettings()
    if (!settings.enableSleepMode) return

    const now = Date.now()
    set((state) => {
      const newCritters = { ...state.critters }
      let changed = false

      Object.keys(newCritters).forEach((userId) => {
        const critter = newCritters[userId]
        if (critter && !critter.isSleeping && !critter.despawning) {
          if (now - critter.lastActivity > settings.sleepTimeout) {
            newCritters[userId] = { ...critter, isSleeping: true, isWalking: false }
            changed = true
          }
        }
      })

      return changed ? { critters: newCritters } : state
    })
  },

  // Start the periodic sleep check
  startSleepCheck: () => {
    const { sleepCheckInterval } = get()
    if (sleepCheckInterval) clearInterval(sleepCheckInterval)

    const interval = setInterval(() => {
      get().checkSleepStates()
    }, 30000) // Check every 30 seconds

    set({ sleepCheckInterval: interval })
  },

  // Stop the sleep check
  stopSleepCheck: () => {
    const { sleepCheckInterval } = get()
    if (sleepCheckInterval) clearInterval(sleepCheckInterval)
    set({ sleepCheckInterval: null })
  },

  // Wake all critters (when sleep mode is disabled)
  wakeAllCritters: () => {
    set((state) => {
      const newCritters = { ...state.critters }
      let changed = false

      Object.keys(newCritters).forEach((userId) => {
        if (newCritters[userId]?.isSleeping) {
          newCritters[userId] = { ...newCritters[userId], isSleeping: false }
          changed = true
        }
      })

      return changed ? { critters: newCritters } : state
    })
  },

  // Clear all critters (local only, for admin panel)
  clearAllCritters: async () => {
    const { broadcasterLogin } = get()
    get().stopRoaming()
    set({ critters: {} })

    if (broadcasterLogin) {
      await critterApi.removeAllCritters()
    }
  }
}))
